import { Box, CardMedia, CircularProgress, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { createUseStyles } from '@/services/createUseStyles';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { Thumbnail } from '../Thumbnail';
import { getAssetFileName } from '@/utils/assets';
import { getFilename } from '@/utils/path';
import classnames from 'classnames';
import { FindMatchesButton } from '../Matches/FindMatchesButton';
import { AddToCompareButton } from '../CompareCart/AddToCompareButton';
import { Ellipsis } from '../Ellipsis';
import { useTenants } from '@/services/tenants/useTenants';
import { Link } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import { useEffect, useState } from 'react';
import { Asset } from '@/api/generatedApi';
import { SyncAlt } from '@mui/icons-material';
import { CopyButton } from '../CopyButton/CopyButton';
import { DependencyContext } from '../AssetSidebar/DependencyTree/DependencyTreeView';
import { AssetSelectionModal } from '../modals/AssetSelectionModal';
import { ConfirmDialog } from '../AssetSidebar/DependencyTree/ConfirmDialog';
import { useResolveDependency } from '@/hooks/api/assets/useResolveDependency';

const DETAILS_HEIGHT = 180;

const useStyles = createUseStyles(theme => ({
  DependencyDetailPopover_paper: {
    marginTop: theme.spacing(2),
    marginLeft: theme.spacing(2),
    paddingRight: theme.spacing(15),
  },
  DependencyDetailPopover_title: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  DependencyDetailPopover_media: {
    height: DETAILS_HEIGHT,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
  },
  DependencyDetailPopover_section: {
    display: 'flex',
    flexDirection: 'row',
    height: DETAILS_HEIGHT,
  },
  DependencyDetailPopover_details: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: theme.spacing(2),
    height: DETAILS_HEIGHT,
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
  },
  DependencyDetailPopover_buttons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  DependencyDetailPopover_link: {
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  DependencyDetailPopover_loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
}));

const PopoverContent = ({
  isLoading,
  asset,
  dependencyContext,
  onClose,
  onSwitchResolution,
}: {
  isLoading: boolean;
  asset?: Asset;
  dependencyContext?: DependencyContext;
  onClose: () => void;
  onSwitchResolution?: () => void;
}) => {
  const c = useStyles();
  const { getPath } = useTenants();

  if (isLoading) {
    return (
      <Box className={c.DependencyDetailPopover_loading}>
        <CircularProgress />
      </Box>
    );
  }
  if (asset) {
    return (
      <Box className={c.DependencyDetailPopover_section}>
        <Link to={getPath(`asset/${asset.id}`)} onClick={onClose} className={c.DependencyDetailPopover_link}>
          <CardMedia className={c.DependencyDetailPopover_media}>
            <Thumbnail asset={asset} size={DETAILS_HEIGHT} noLink />
          </CardMedia>
        </Link>
        <Box className={c.DependencyDetailPopover_details}>
          <div>
            <Typography variant='subtitle1' className={c.DependencyDetailPopover_title}>
              {asset.hasDependencies ? 'Selected Assembly' : 'Selected Part'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Link to={getPath(`asset/${asset.id}`)} onClick={onClose} className={c.DependencyDetailPopover_link}>
                <Ellipsis
                  text={getAssetFileName(asset)}
                  sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}
                  tooltipProps={{ title: getAssetFileName(asset) }}
                />
              </Link>
              <CopyButton text={getAssetFileName(asset)} tooltip='Copy name' sx={{ p: '2px' }} />
            </Box>
            {dependencyContext && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Typography variant='body2' sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  Resolves <strong>{getFilename(dependencyContext.dependencyPath)}</strong> in{' '}
                  <strong>{dependencyContext.assemblyName}</strong>.
                </Typography>
                {onSwitchResolution && (
                  <Tooltip title='Switch resolution'>
                    <IconButton
                      size='small'
                      color='primary'
                      onClick={onSwitchResolution}
                      data-testid='switch-resolution-button'
                      sx={{ ml: 0.5 }}
                    >
                      <SyncAlt sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </div>

          <div className={c.DependencyDetailPopover_buttons}>
            <FindMatchesButton asset={asset} />
            <AddToCompareButton asset={asset} />
          </div>
        </Box>
      </Box>
    );
  }
  return;
};

export interface DependencyDetailPopoverProperties {
  open: boolean;
  anchorEl: HTMLElement | null;
  selectedTreeItemId: string;
  assetTenantId?: string;
  dependencyContext?: DependencyContext;
  onClose: () => void;
  paperClassName?: string;
}

export const DependencyDetailPopover = ({
  open,
  anchorEl,
  selectedTreeItemId,
  assetTenantId,
  dependencyContext,
  onClose,
  paperClassName,
}: DependencyDetailPopoverProperties) => {
  const c = useStyles();
  const { showToast } = useToast();
  const { getTenantId } = useTenants();
  const [isAssetSelectionOpen, setIsAssetSelectionOpen] = useState(false);
  const [pendingAsset, setPendingAsset] = useState<Asset | undefined>();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { resolveDependency } = useResolveDependency({
    onSuccess: () => {
      showToast({ message: 'Dependency resolved successfully', type: 'success' });
      onClose();
    },
    onError: errorMessage => {
      showToast({ message: errorMessage, type: 'error' });
    },
  });

  const { asset, isLoading, error } = useGetAsset({ assetId: selectedTreeItemId, assetTenantId });

  useEffect(() => {
    if (error) {
      showToast({
        message: 'Failed to load asset details',
        type: 'error',
      });
      onClose();
    }
  }, [error, onClose, showToast]);

  if (error) {
    return;
  }

  const handleSwitchResolution = () => {
    setIsAssetSelectionOpen(true);
  };

  const handleAssetSelect = (selectedAsset: Asset) => {
    setIsAssetSelectionOpen(false);
    setPendingAsset(selectedAsset);
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    if (!pendingAsset || !dependencyContext) return;
    resolveDependency({
      assetId: dependencyContext.assemblyId,
      dependencyPath: dependencyContext.dependencyPath,
      resolvedAssetId: pendingAsset.id,
    });
    setConfirmOpen(false);
    setPendingAsset(undefined);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
    setPendingAsset(undefined);
  };

  const handleBack = () => {
    setConfirmOpen(false);
    setPendingAsset(undefined);
    setIsAssetSelectionOpen(true);
  };

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        onAbort={onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{ paper: classnames(c.DependencyDetailPopover_paper, paperClassName) }}
        disableRestoreFocus
        // By default the Popover's Modal root overlays the viewer iframe and swallows pointer
        // events (including right-click). The below make the root pointer-transparent so the
        // viewer stays interactive; the paper keeps pointer-events so it remains clickable.
        hideBackdrop
        sx={{ pointerEvents: 'none' }}
        slotProps={{ paper: { sx: { pointerEvents: 'auto' } } }}
      >
        <PopoverContent
          isLoading={isLoading}
          asset={asset}
          dependencyContext={dependencyContext}
          onClose={onClose}
          onSwitchResolution={
            dependencyContext && asset?.tenantId === getTenantId() ? handleSwitchResolution : undefined
          }
        />
      </Popover>
      <AssetSelectionModal
        open={isAssetSelectionOpen}
        onClose={() => setIsAssetSelectionOpen(false)}
        onAssetSelect={handleAssetSelect}
        title='Switch Resolved Asset'
        initialFolderId={asset?.folderId}
        description={`Select a different asset to resolve "${getFilename(dependencyContext?.dependencyPath ?? '')}".`}
      />
      <ConfirmDialog
        open={confirmOpen}
        mode='switch'
        dependencyName={getFilename(dependencyContext?.dependencyPath ?? '')}
        targetName={pendingAsset?.path ?? ''}
        currentResolvedName={asset ? getAssetFileName(asset) : undefined}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        onBack={handleBack}
      />
    </>
  );
};
